import { useEffect, useRef, useState } from 'react';
import type { Bulle } from '../partage/api/types';
import {
  ADRESSE,
  APPELER,
  CONTACT,
  COPIE,
  COPIER_LE_NUMERO,
  corrigerLaPiece,
  E_MAIL,
  ECRIRE_UNE_NOTE,
  ITINERAIRE,
  NOTE_DU_CONTACT,
  RENOMMER_LA_BULLE,
  SMS,
  SON_E_MAIL,
} from '../partage/vocabulaire';
import { useNoteEcrite } from './ChampsNotes';
import { appelerLe, initialeDuContact, itineraireVers, messageA, smsA } from './ficheEnMots';
import { Plus } from './ReglagesDeLaFiche';
import { TitreDeFiche } from './TitreDeFiche';
import type { MecaniqueDeLaFiche } from './useMecaniqueDeLaFiche';
import './ContactDeLaFiche.css';

/**
 * La fiche Contact (chantier types-de-fiche, modification 04 ; maquette du carnet
 * `fiche-quatre-types.html`, colonne « 3. Contact » : `.carte-visite`, `.avatar-c`, `.metier`,
 * `.coord`, `.actions`, `.rubrique`). La carte de visite : l'initiale dans un rond, le nom du
 * contact en titre, et dessous, à la place du métier, le titre de la bulle s'il diffère du nom.
 * Puis le numéro en grand, qui se copie d'un clic, l'e-mail, l'adresse ; « Itinéraire » et
 * « E-mail » ; la note. Ni SMS, ni Appeler, ni « Dans mes projets » (décisions d'Igor du
 * 2026-10-20). Le nom, chaque crayon et « + Contact » (sans contact) ouvrent l'intercalaire
 * Contact des Réglages, où ils se corrigent (chantier reglages-de-la-fiche, modification 03) : la
 * face montre, les Réglages changent.
 *
 * Au téléphone (`auPouce`, chantier telephone-fiche, 01, maquette `fiche-au-telephone.html`,
 * `.carte-v`, `.gros`, `.coord`) : la même carte, puis Appeler, SMS et Itinéraire en trois
 * boutons égaux, Appeler en premier et en foncé, à la place de « Itinéraire » et « E-mail ».
 */
export function ContactDeLaFiche({
  bulle,
  mecanique,
  onPoser,
  onChanger,
  auPouce = false,
}: {
  bulle: Bulle;
  mecanique: MecaniqueDeLaFiche;
  /** Ouvrir l'intercalaire Contact des Réglages. */
  onPoser: () => void;
  onChanger: (champs: Partial<Bulle>) => void;
  /** La fiche du téléphone : les trois gestes du pouce sous la carte. */
  auPouce?: boolean;
}) {
  const nom = bulle.contactNom?.trim() || null;
  const tel = bulle.contactTel?.trim() || null;
  const email = bulle.contactEmail?.trim() || null;
  const adresse = bulle.lieuTexte?.trim() || null;
  const aUnContact = !!(nom || tel || email);
  const renommer = mecanique.renommer;

  /* Le titre de la bulle, qui se corrige sur place, comme celui de la fiche Classique. */
  const titre = (
    <TitreDeFiche
      titre={bulle.titre}
      edition={renommer}
      onEdition={mecanique.setRenommer}
      onRenommer={(t) => onChanger({ titre: t })}
    />
  );

  return (
    <section className="contact-fiche" aria-label={CONTACT}>
      {/* L'essentiel, d'un tenant : couché, la colonne de gauche ; ailleurs, sans boîte à lui. */}
      <div className="contact-essentiel">
        <div className="contact-carte">
          {nom && (
            <span className="contact-avatar" aria-hidden="true">
              {initialeDuContact(nom)}
            </span>
          )}
          <div className="contact-qui">
            {nom ? (
              <>
                <h2 className="contact-nom">
                  <button
                    type="button"
                    className="fiche-titre"
                    title={corrigerLaPiece(CONTACT.toLowerCase())}
                    onClick={onPoser}
                  >
                    {nom}
                  </button>
                </h2>
                {(renommer || bulle.titre !== nom) && <div className="contact-metier">{titre}</div>}
              </>
            ) : (
              <div className="contact-titre">{titre}</div>
            )}
          </div>
          {!renommer && (
            <button
              type="button"
              className="fiche-crayon"
              aria-label={RENOMMER_LA_BULLE}
              title={RENOMMER_LA_BULLE}
              onClick={() => mecanique.setRenommer(true)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path d="M4 20h4L19 9l-4-4L4 16v4z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>

        {auPouce && (tel || adresse) && (
          <div className="contact-gros">
            {tel && (
              <a className="contact-gros-geste est-principal" href={appelerLe(tel)}>
                {APPELER}
              </a>
            )}
            {tel && (
              <a className="contact-gros-geste" href={smsA(tel)}>
                {SMS}
              </a>
            )}
            {adresse && (
              <a className="contact-gros-geste" href={itineraireVers(adresse)} target="_blank" rel="noopener noreferrer">
                {ITINERAIRE}
              </a>
            )}
          </div>
        )}

        {!aUnContact && (
          <button type="button" className="contact-poser" onClick={onPoser}>
            <Plus />
            {CONTACT}
          </button>
        )}

        {tel && (
          <div className="contact-coord">
            <span className="contact-icone" aria-hidden="true">
              📞
            </span>
            <NumeroACopier numero={tel} />
            <Corriger mot={CONTACT} onPoser={onPoser} />
          </div>
        )}
        {email && (
          <div className="contact-coord">
            <span className="contact-icone" aria-hidden="true">
              ✉
            </span>
            <b className="contact-valeur">{email}</b>
            <Corriger mot={SON_E_MAIL} onPoser={onPoser} />
          </div>
        )}
        {adresse && (
          <div className="contact-coord">
            <span className="contact-icone" aria-hidden="true">
              📍
            </span>
            <b className="contact-valeur">{adresse}</b>
            <Corriger mot={ADRESSE} onPoser={onPoser} />
          </div>
        )}


        {!auPouce && (adresse || email) && (
          <div className="contact-actions">
            {adresse && (
              <a className="contact-geste" href={itineraireVers(adresse)} target="_blank" rel="noopener noreferrer">
                {ITINERAIRE}
              </a>
            )}
            {email && (
              <a className="contact-geste" href={messageA(email)}>
                {E_MAIL}
              </a>
            )}
          </div>
        )}
      </div>

      <NoteDuContact key={`note-${bulle.id}`} valeur={bulle.notes} onChanger={(notes) => onChanger({ notes })} />
    </section>
  );
}

/** Le numéro en grand : un clic le copie, et « Copié » le dit un instant. */
function NumeroACopier({ numero }: { numero: string }) {
  const [copie, setCopie] = useState(false);
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (minuteur.current) clearTimeout(minuteur.current);
    },
    [],
  );

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(numero);
    } catch {
      return; // Le presse-papiers refusé : rien ne prétend que c'est copié.
    }
    setCopie(true);
    if (minuteur.current) clearTimeout(minuteur.current);
    minuteur.current = setTimeout(() => setCopie(false), 1600);
  };

  return (
    <>
      <button
        type="button"
        className="contact-numero"
        title={COPIER_LE_NUMERO}
        aria-label={`${COPIER_LE_NUMERO} : ${numero}`}
        onClick={copier}
      >
        {numero}
      </button>
      <span className="contact-copie" role="status">
        {copie ? COPIE : ''}
      </span>
    </>
  );
}

/** Le crayon d'une ligne : il ouvre l'intercalaire Contact des Réglages. */
function Corriger({ mot, onPoser }: { mot: string; onPoser: () => void }) {
  const nom = corrigerLaPiece(mot.toLowerCase());
  return (
    <button type="button" className="contact-corriger" aria-label={nom} title={nom} onClick={onPoser}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
        <path d="M4 20h4L19 9l-4-4L4 16v4z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

/** « Note », dessous (maquette `.rubrique`) : le texte à même la carte, qui s'écrit sur place. */
function NoteDuContact({ valeur, onChanger }: { valeur: string | null; onChanger: (valeur: string | null) => void }) {
  const note = useNoteEcrite(valeur, onChanger);
  return (
    <div className="contact-rubrique">
      <label className="contact-rubrique-nom" htmlFor="contact-note">
        {NOTE_DU_CONTACT}
      </label>
      <textarea
        id="contact-note"
        className="contact-note"
        data-tuto="champ-notes"
        value={note.brouillon}
        placeholder={ECRIRE_UNE_NOTE}
        onChange={(e) => note.ecrire(e.target.value)}
        onBlur={note.quitter}
      />
    </div>
  );
}
